<template>
    <div>
        <div class="filters-bar-container">
            <v-container class="pb-0">
                <FiltersBar ref="filtersBar" searchClass="pb-3" wrapperClass="pb-0" alwaysMinified :minWidth="6" :search="search" @update:search="search = $event"></FiltersBar>
            </v-container>
            <v-expansion-panels
                class="expansion-panels"
                v-model="panel"
                multiple
                focusable
                accordion
            >
                <v-expansion-panel :disabled="selected.length === 0">
                    <v-expansion-panel-header>
                        <v-row no-gutters>
                            <v-col>
                                {{ $t('tracks.message.selected') }}
                            </v-col>
                            <v-col cols="auto" class="mr-3">
                                <v-badge :content="selected.length" :value="selected.length" color="secondary" inline />
                            </v-col>
                        </v-row>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <TreeView v-model="selected" :items="selected" />
                    </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <v-row no-gutters>
                            <v-col>
                                {{ $t('tracks.message.employees') }}
                            </v-col>
                            <v-col cols="auto" class="mr-4">
                                <small>{{ $t('tracks.message.displaying') }}: <strong>{{ elements.length }}/{{ totalItemsAmount }}</strong></small>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon
                                            color="secondary"
                                            v-bind="attrs"
                                            v-on="on"
                                            size="medium"
                                            class="ml-1"
                                        >
                                        {{ ICONS.INFORMATION }}
                                        </v-icon>
                                    </template>
                                    <span>{{ $t('tracks.message.displaying_tooltip') }}</span>
                                </v-tooltip>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <TreeView v-model="selected" :items="elements" />
                        <SkeletonLoaderTree v-if="isLoading" />
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
    </div>
</template>

<script>
import AutocompleteFilter from '@/models/filters/autocompleteFilter';
import debounce from 'lodash.debounce';
import debouncedEmployees from '@/mixins/debounced/debouncedEmployees';
import debouncedEmployeesGroups from '@/mixins/debounced/debouncedEmployeesGroups';
import debouncedCustomers from '@/mixins/debounced/debouncedCustomers';
import debouncedSites from '@/mixins/debounced/debouncedSites';
import debouncedLocations from '@/mixins/debounced/debouncedLocations';
import { ICONS } from '@/constants/icons';
import { mapState } from 'vuex';
import filtering from '@/mixins/filtering';
import FiltersBar from '@/components/widgets/tables/FiltersBar.vue';
import { FILTERS, FILTERS_SEARCHING } from '@/constants/filtersModelNames';
import TreeView from '@/components/treeView/TreeView.vue';
import SkeletonLoaderTree from './SkeletonLoaderTree.vue';

export default {
    name: 'LazyElementsTree',
    mixins: [
        filtering,
        debouncedEmployees,
        debouncedEmployeesGroups,
        debouncedCustomers,
        debouncedSites,
        debouncedLocations
    ],
    components: { FiltersBar, TreeView, SkeletonLoaderTree },
    data: () => ({
        search: null,
        selected: [],
        ICONS,
        debouncedFetchElements: null,
        pagination: {
            page: 1
        },
        initialed: false,
        panel: [1]
    }),
    props: {
        elements: Array,
        initiallySelected: Array,
        isLoading: Boolean
    },
    computed: {
        ...mapState('filters', [
            'filters',
            'filtersSearching'
        ]),
        ...mapState('manageTrack', [
            'trackData'
        ]),
        ...mapState('employees', [
            'totalItemsAmount'
        ]),
        filtersConfig () {
            return [
                new AutocompleteFilter({
                    model: FILTERS.EMPLOYEE_GROUP_ID,
                    label: this.$t('navigation.employees_groups'),
                    loading: true,
                    multiple: true,
                    search: FILTERS_SEARCHING.EMPLOYEES_GROUPS_NAME,
                    lazy: this.getEmployeesGroupsLazyLoading,
                    searchCallback: this.debouncedGetEmployeesGroups,
                    items: {
                        obj: 'getters',
                        module: 'employeesGroups',
                        prop: 'filterableEmployeesGroups'
                    }
                }),
                new AutocompleteFilter({
                    model: FILTERS.CUSTOMER_ID,
                    label: this.$t('labels.customer'),
                    loading: false,
                    multiple: true,
                    search: FILTERS_SEARCHING.CUSTOMER,
                    lazy: this.getCustomersLazyLoading,
                    referralCallback: this.referralCustomerCallback,
                    searchCallback: this.debouncedGetCustomers,
                    items: {
                        obj: 'getters',
                        module: 'customers',
                        prop: 'listingCustomers'
                    }
                }),
                new AutocompleteFilter({
                    model: FILTERS.SITE_ID,
                    label: this.$t('labels.site'),
                    loading: false,
                    multiple: true,
                    disabled: true,
                    successMessages: this.$t('messages.choose_customer'),
                    search: FILTERS_SEARCHING.SITE_PER_CUSTOMER,
                    lazy: this.getSitesLazyLoading,
                    referralCallback: this.referralSiteCallback,
                    searchCallback: this.debouncedGetSites,
                    searchOptions: {
                        reference: FILTERS.CUSTOMER_ID
                    },
                    items: {
                        obj: 'getters',
                        module: 'sites',
                        prop: 'sites'
                    }
                }),
                new AutocompleteFilter({
                    model: FILTERS.LOCATION_ID,
                    label: this.$t('labels.location'),
                    loading: false,
                    multiple: true,
                    disabled: true,
                    successMessages: this.$t('messages.choose_site'),
                    search: FILTERS_SEARCHING.LOCATION,
                    lazy: this.getLocationsLazyLoading,
                    searchCallback: this.debouncedGetLocations,
                    searchOptions: {
                        reference: FILTERS.SITE_ID
                    },
                    items: {
                        obj: 'getters',
                        module: 'locations',
                        prop: 'locations'
                    }
                })
            ];
        }
    },
    watch: {
        selected: {
            handler (val, oldVal) {
                if (val.length === 0) {
                    this.panel = this.panel.filter(panel => panel !== 0);
                }

                if (JSON.stringify(val) === JSON.stringify(oldVal) && val.length !== 0) return;
                this.$store.dispatch('cyclicRevisions/setSelectedTracksElements', val);
            }
        },
        search: {
            handler (val, oldVal) {
                if ((val && val.length >= 2) || (oldVal && val === '')) {
                    this.debouncedFetchElements();
                }
            }
        },
        initiallySelected: {
            handler (val) {
                if (this.initialed) return;

                this.selected = val;
                this.initialed = true;
            }
        }
    },
    methods: {
        selectElement (element) {
            if (this.selected.includes(element)) {
                this.selected = this.selected.filter(i => i.id !== element.id);
            } else {
                this.selected = [...this.selected, element];
            }
        },
        removeSelected (item) {
            this.selected = this.selected.filter(i => i.id !== item.id);
        },
        generateFilters () {
            const params = {};

            this.filtersConfig.forEach(filter => {
                const f = filter.model;
                if ([FILTERS.SITE_ID, FILTERS.CUSTOMER_ID].includes(f)) {
                    return;
                }
                if (this.filters[f] && this.filters[f].length > 0) {
                    params[`filter[${f}]`] = this.filters[f].join(',');
                }
            });

            return params;
        },
        fetchElements () {
            const params = {
                'filter[is_item]': true,
                'page[number]': this.pagination.page,
                ...this.generateFilters()
            };

            if (this.search) {
                params['filter[item_search]'] = this.search;
            }
            this.$emit('get-elements', params);
        },
        setFiltersWatcher (callback) {
            this.localConfig.forEach(filter => {
                const el = filter.model;
                this.$watch(`${this.filterPropertyName}.${el}`, function (newVal) {
                    if (newVal) {
                        if (this.pagination) {
                            this.pagination.page = 1;
                        }
                        callback();
                        if (filter.hasProperty('referralCallback')) {
                            filter.referralCallback(newVal);
                        }
                    } else if (newVal === null) {
                        callback();
                    }
                });
            });
        }
    },
    mounted () {
        this.$store.commit('filters/SET_CONFIG', this.filtersConfig);
        this.$store.dispatch('employeesGroups/getEmployeesGroups')
            .then(() => {
                this.filtersConfig.find(el => el.model === FILTERS.EMPLOYEE_GROUP_ID).loading = false;
                this.$store.commit('filters/SET_CONFIG', this.filtersConfig);
            });
        this.debouncedFetchElements = debounce(this.fetchElements, 1000);
        this.setFiltersWatcher(this.debouncedFetchElements);
    },

    beforeUnmount () {
        this.$store.dispatch('filters/clearFiltersParams');
        this.search = null;
        this.debouncedFetchElements = null;
    }

};
</script>

<style scoped lang="scss">
    .filters-bar-container {
        position: sticky;
        top: 0;
        z-index: 1;
        background-color: #fff;
        border-bottom: 1px solid #e0e0e0;
    }
    .expansion-panels {
        z-index: 0;

        ::v-deep {
            .v-expansion-panel-content__wrap {
                padding: 0;
            }

            .v-expansion-panel-header {
                padding: 12px;
            }
        }
    }
    ::v-deep {
         .v-treeview-node--disabled {
            display: none;
         }
    }
</style>
